import { useEffect, useRef, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/services/api';
import { useAuthStore } from '@/store/auth.store';
import { toast } from '@/components/common/Toast';

const ACTIVITY_EVENTS: (keyof DocumentEventMap)[] = [
  'mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click',
];

/**
 * Monitors user activity and auto-logs-out after the configured idle timeout.
 * The timeout value is fetched from the server (admin-configurable, default 30 min).
 * Shows a warning toast 60 seconds before logout.
 */
export function useSessionTimeout() {
  const token  = useAuthStore((s) => s.token);
  const logout = useAuthStore((s) => s.logout);

  // Fetch session timeout from public endpoint (cached for 5 min)
  const { data } = useQuery({
    queryKey: ['session-timeout'],
    queryFn: () => api.get<{ data: { sessionTimeoutMinutes: number } }>('/settings/session-timeout')
      .then(r => r.data.data.sessionTimeoutMinutes),
    staleTime: 5 * 60 * 1000,
    enabled: !!token,
  });

  const timeoutMinutes = data ?? 30;
  const timerRef     = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningRef   = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningShown = useRef(false);

  const doLogout = useCallback(() => {
    logout();
    window.location.href = '/login?reason=timeout';
  }, [logout]);

  const resetTimer = useCallback(() => {
    if (!token) return;
    if (timerRef.current)   clearTimeout(timerRef.current);
    if (warningRef.current) clearTimeout(warningRef.current);
    warningShown.current = false;

    const ms        = timeoutMinutes * 60 * 1000;
    const warningMs = Math.max(ms - 60_000, 0); // warn 60s before

    warningRef.current = setTimeout(() => {
      warningShown.current = true;
      // Visibly warn the user 60s before auto-logout so they can stay signed in.
      toast.warning('You will be signed out soon due to inactivity. Move your mouse or press a key to stay signed in.', 60_000);
    }, warningMs);

    timerRef.current = setTimeout(doLogout, ms);
  }, [token, timeoutMinutes, doLogout]);

  useEffect(() => {
    if (!token) return;

    resetTimer();

    const handler = () => resetTimer();
    for (const evt of ACTIVITY_EVENTS) {
      document.addEventListener(evt, handler, { passive: true });
    }

    return () => {
      for (const evt of ACTIVITY_EVENTS) {
        document.removeEventListener(evt, handler);
      }
      if (timerRef.current)   clearTimeout(timerRef.current);
      if (warningRef.current) clearTimeout(warningRef.current);
    };
  }, [token, resetTimer]);
}
